import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { SystemReviewService } from 'app/cm/services/system-review-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { SaveIllness } from 'app/cm/admin/system-illness/save-illness';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class SystemIllnessDetail extends ComponentBase {

  ////****************** Fields and Properties ************************************////
  @bindable.string()
  protected systemId: string = "";

  @bindable.boolean()
  protected includeInactive: boolean = false;

  protected illnesses: ReadonlyArray<CM.SystemIllness> = [];

  private sorting = new Sorting();
  protected resultCount = 0;
  protected pageSize = 25;

  protected validate = validate;

  ////****************** Element References ***************************************////
  protected $pager!: EssPagerCustomElement;
  protected $form!: IForm;
  protected $saveIllnessModal!: IPopup;
  protected $illnessForm!: Element;
  protected $saveIllness!: SaveIllness;

  ////****************** Lifecycle events (in order of execution) *****************////
  constructor(
    private readonly service: SystemReviewService,
    private readonly toaster: Toaster,
    ...rest: any[]) {

    super(...rest);

    this.applyDefaultSort();
  }

  protected bind() {
    this.reload();
  }

  protected saveIllness(illness: CM.SystemIllness): void {
    this.$saveIllness.illnessId = illness?.id || '';
    this.$saveIllness.illnessText = illness?.description || '';
    this.$saveIllness.systemId = this.systemId;
    this.$saveIllnessModal.open();
  }

  protected formSuccess({ detail }: FormSuccessCustomEvent) {
    this.$saveIllnessModal.close();

    // reload grid and reset page
    this.reload();
  }

  ////****************** Events ***************************************************////
  public async reload(): Promise<void> {
    await this.loadIllnessesForSystem();
  }

  protected applySort(event: SortChangeEvent): void {
    const { property, direction } = event.detail;
    const { sorting } = this;
    if(direction === null) {
      sorting.remove(property);
      if(this.sorting.entries.length == 0) {
        this.applyDefaultSort();
      }
    } else {
      sorting.add({
        reference: property,
        direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
      });
    }

    this.reload();
    //Call stop Propagation to prevent parent table from reload and losing sort
    event.stopPropagation();
  }

  ////****************** Functions ************************************************////
  protected applyDefaultSort(): void {
    this.sorting.entries = [
      {
        reference: 'description',
        direction: SortDirection.Ascending
      }
    ];
  }

  protected saveIllnessClose(): void {
    if(this.$form.reset) this.$form.reset();
    this.$saveIllnessModal.close();
  }

  ////****************** Service Calls ********************************************////
  @apiCall
  private async loadIllnessesForSystem(): Promise<void> {
    if(!this.systemId) return;

    const includes: string[] = [

    ];
    const fields: string[] = [
      "description"
    ];
    const options: Partial<IQueryLoadOptions> = {
      sorting: this.sorting,
      top: this.pageSize,
      page: this.$pager?.page || 0,
      includeInactive: this.includeInactive
    };

    try {
      this.state.beginLoad();

      const response = await this.service.loadIllnessesForSystem(this.systemId, options, includes, fields);
      this.illnesses = response.results;
      this.resultCount = response.count || 0;
    } catch(error) {
      throw error;
    } finally {
      this.state.endLoad();
    }
  }

  @apiCall
  protected async activateIllness(illness: CM.SystemIllness): Promise<void> {
    if(!illness.id) return;

    // Show confirmation dialog only if deactivating
    if(illness.isActive) {
      const result = await confirmation.showDeactivate('cm:systemIllness.$', 1, false);
      if(result.dialogResponse !== confirmation.options.OK) { return; }
    }

    let isSuccessful = false;
    try {
      this.state.beginSave();
      const response = await this.service.activateSystemIllness(illness.id, !illness.isActive);
      isSuccessful = response.isSuccessful;
      await this.reload();
    }
    catch(error) {
      throw error;
    }
    finally {
      this.toaster.standard.activate('cm:systemIllness.$', !illness.isActive, isSuccessful);
      this.state.endSave();
    }
  }
}
